import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Mail, MailX } from "lucide-react";
import CalendarAdmin from "@/components/admin/CalendarAdmin";
import TesterAdmin from "@/components/admin/TesterAdmin";

export default function AdminDashboard() {
  const [tab, setTab] = useState("calendar");
  const [settings, setSettings] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const loadSettings = async () => {
    const [s, n] = await Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/notifications"),
    ]);
    setSettings(s.data);
    setNotifications(n.data);
  };
  useEffect(() => { loadSettings(); }, []);

  const updateSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      const { data } = await api.put("/admin/settings", {
        notifications_enabled: next.notifications_enabled,
        notify_on_comment: next.notify_on_comment,
        notify_on_photo: next.notify_on_photo,
      });
      setSettings(data);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const markRead = async () => {
    await api.post("/admin/notifications/mark-read");
    setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <header>
        <div className="label-eyebrow mb-3">Admin</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight">Build the <em className="italic text-[hsl(var(--primary))]">practice</em>.</h1>
        <p className="mt-6 text-stone-600 max-w-2xl leading-relaxed">Curate months, recipes, and movements — one calendar day at a time.</p>
      </header>

      <div className="flex border-b border-border" data-testid="admin-tabs">
        {[
          { k: "calendar", label: "Calendar" },
          { k: "testers", label: "Testers" },
          { k: "notifications", label: "Notifications" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`px-6 py-3 text-sm tracking-wide transition-colors ${tab === t.k ? "text-[hsl(var(--foreground))] border-b-2 border-[hsl(var(--primary))] -mb-px" : "text-stone-500 hover:text-[hsl(var(--foreground))]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "calendar" && <CalendarAdmin />}

      {tab === "testers" && <TesterAdmin />}

      {tab === "notifications" && (
        <section className="grid md:grid-cols-12 gap-0 border-t border-b border-border" data-testid="admin-notifications-section">
          <div className="md:col-span-5 p-8 md:p-10 border-r border-border">
            <div className="label-eyebrow mb-4">Notifications</div>
            <h2 className="font-serif text-3xl mb-4">Peace on your own terms.</h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-8">Get pinged when members join the conversation — or turn it off when you need a break.</p>
            {settings && (
              <div className="space-y-4">
                <ToggleRow
                  icon={settings.notifications_enabled ? Bell : BellOff}
                  label="All notifications"
                  sub={settings.notifications_enabled ? "You'll be alerted about activity." : "Silent — nothing will notify you."}
                  on={settings.notifications_enabled}
                  onChange={(v) => updateSettings({ notifications_enabled: v })}
                  testid="toggle-notifications"
                />
                <ToggleRow icon={Mail} label="New comments" sub="Alert me when a member posts a comment or question." on={settings.notify_on_comment} disabled={!settings.notifications_enabled} onChange={(v) => updateSettings({ notify_on_comment: v })} testid="toggle-comment" />
                <ToggleRow icon={Mail} label="New photos" sub="Alert me when a member attaches a photo." on={settings.notify_on_photo} disabled={!settings.notifications_enabled} onChange={(v) => updateSettings({ notify_on_photo: v })} testid="toggle-photo" />
                {!settings.email_delivery_configured && (
                  <div className="border border-border p-4 text-xs text-stone-600 flex gap-3">
                    <MailX className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <strong>Email delivery: MOCKED.</strong> Notifications appear in the inbox on the right. To send real emails to <em>{settings.admin_email}</em>, add <code className="bg-[hsl(var(--muted))] px-1">RESEND_API_KEY</code> to the backend.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-7 p-8 md:p-10">
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow">Notification inbox</div>
              {notifications.some((n) => !n.read) && (
                <button onClick={markRead} data-testid="btn-mark-read" className="text-xs text-stone-500 hover:text-[hsl(var(--primary))]">Mark all as read</button>
              )}
            </div>
            {notifications.length === 0 ? (
              <p className="text-stone-500 italic font-serif text-xl">Nothing yet — quiet on the wire.</p>
            ) : (
              <ul className="divide-y divide-border max-h-[420px] overflow-y-auto" data-testid="notif-list">
                {notifications.map((n) => (
                  <li key={n.id} className="py-4" data-testid={`notif-${n.id}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className={`font-serif ${!n.read ? "text-[hsl(var(--foreground))]" : "text-stone-500"}`}>{n.subject}</span>
                      <span className="text-xs text-stone-400 whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-stone-600 mt-1 leading-relaxed">{n.body}</p>
                    <div className="mt-1 flex items-center gap-3">
                      <span className={`text-[10px] uppercase tracking-widest ${n.delivery === "sent" ? "text-[hsl(var(--secondary))]" : n.delivery === "failed" ? "text-[hsl(var(--destructive))]" : "text-stone-400"}`}>{n.delivery === "logged" ? "LOGGED (MOCKED)" : n.delivery.toUpperCase()}</span>
                      {!n.read && <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))]">Unread</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function ToggleRow({ icon: Icon, label, sub, on, onChange, disabled, testid }) {
  return (
    <div className={`flex items-start justify-between gap-4 py-3 border-b border-border ${disabled ? "opacity-50" : ""}`}>
      <div className="flex gap-3">
        <Icon className="w-4 h-4 text-stone-500 mt-1" strokeWidth={1.5} />
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-stone-500 mt-0.5">{sub}</div>
        </div>
      </div>
      <button
        onClick={() => !disabled && onChange(!on)}
        disabled={disabled}
        data-testid={testid}
        aria-pressed={on}
        className={`shrink-0 relative w-11 h-6 border transition-colors ${on ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))]" : "bg-[hsl(var(--background))] border-border"}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${on ? "left-6" : "left-0.5"}`} />
      </button>
    </div>
  );
}
